import React, { useState, useEffect, useRef, useCallback } from 'react';
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiX, FiTrash2, FiSearch, FiVideo, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { formatView } from '../../../components/formatView';
import generateCaptcha from '../../../components/captcha';
import Navbar from './Navbar';

const Reels = () => {
    // States
    const [loader, setLoader] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const limit = 10;
    const [search, setSearch] = useState('');
    const [reelsData, setReelsData] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [previewVideo, setPreviewVideo] = useState(null);
    const fileInputRef = useRef(null);

    // Bulk Delete States
    const [selectedIds, setSelectedIds] = useState([]);

    // Constants
    const MAX_FILES = 10;
    const MAX_SIZE_MB = 10;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const ALLOWED_TYPES = ['video/mp4'];

    // Fetch reels data
    const fetchData = useCallback(async () => {
        try {
            setLoader(true);
            const resp = await Calling.getReels(page, limit, search);
            if (resp?.status) {
                setReelsData(resp?.data || []);
                setPagination(resp?.pagination || null);
                setSelectedIds([]); // Clear selection on data refresh
            }
        } catch (error) {
            console.log('Error:', error);
            toast.error('Something went wrong while fetching reels');
        } finally {
            setLoader(false);
        }
    }, [page, search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (page === 1) {
                fetchData();
            } else {
                setPage(1);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Validate file
    const validateFile = (file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            return { valid: false, error: `${file.name}: Invalid file type. Only MP4 allowed.` };
        }
        if (file.size > MAX_SIZE_BYTES) {
            return { valid: false, error: `${file.name}: File size exceeds ${MAX_SIZE_MB}MB limit.` };
        }
        return { valid: true };
    };

    // Handle file selection
    const handleFileSelect = (files) => {
        const fileArray = Array.from(files);

        // Check max files limit
        if (selectedFiles.length + fileArray.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} files allowed at once`);
            return;
        }

        const validFiles = [];
        const errors = [];

        fileArray.forEach(file => {
            const validation = validateFile(file);
            if (validation.valid) {
                // Check for duplicates
                const isDuplicate = selectedFiles.some(f => f.file.name === file.name && f.file.size === file.size);
                if (!isDuplicate) {
                    validFiles.push({
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        file,
                        preview: URL.createObjectURL(file),
                        status: 'pending', // pending, uploading, success, error
                        progress: 0,
                        error: null
                    });
                }
            } else {
                errors.push(validation.error);
            }
        });

        if (errors.length > 0) {
            errors.forEach(err => toast.error(err));
        }

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            setShowUploadModal(true);
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        handleFileSelect(files);
    };

    // Remove file from selection
    const removeFile = (id) => {
        setSelectedFiles(prev => {
            const file = prev.find(f => f.id === id);
            if (file?.preview) {
                URL.revokeObjectURL(file.preview);
            }
            return prev.filter(f => f.id !== id);
        });
    };

    // Upload single file using Calling service
    const uploadSingleFile = async (fileObj) => {
        try {
            // Set status to uploading
            setSelectedFiles(prev => prev.map(f =>
                f.id === fileObj.id ? { ...f, status: 'uploading', progress: 50 } : f
            ));

            const formData = new FormData();
            formData.append('video', fileObj.file);

            const resp = await Calling.addReels(formData);

            if (resp?.status) {
                setSelectedFiles(prev => prev.map(f =>
                    f.id === fileObj.id ? { ...f, status: 'success', progress: 100 } : f
                ));
                return resp;
            } else {
                setSelectedFiles(prev => prev.map(f =>
                    f.id === fileObj.id ? { ...f, status: 'error', error: resp?.message || 'Upload failed' } : f
                ));
                throw new Error(resp?.message || 'Upload failed');
            }
        } catch (error) {
            setSelectedFiles(prev => prev.map(f =>
                f.id === fileObj.id ? { ...f, status: 'error', error: error.message || 'Upload failed' } : f
            ));
            throw error;
        }
    };

    // Upload all files
    const uploadAllFiles = async () => {
        const pendingFiles = selectedFiles.filter(f => f.status === 'pending' || f.status === 'error');

        if (pendingFiles.length === 0) {
            toast.info('No files to upload');
            return;
        }

        setUploadLoader(true);
        let successCount = 0;
        let errorCount = 0;

        for (const fileObj of pendingFiles) {
            try {
                await uploadSingleFile(fileObj);
                successCount++;
            } catch (error) {
                errorCount++;
                setSelectedFiles(prev => prev.map(f =>
                    f.id === fileObj.id ? { ...f, status: 'error', error: error.message } : f
                ));
            }
        }

        setUploadLoader(false);

        if (successCount > 0) {
            toast.success(`${successCount} reel(s) uploaded successfully!`);
            fetchData();
        }
        if (errorCount > 0) {
            toast.error(`${errorCount} reel(s) failed to upload`);
        }

        // Clear successful uploads
        setTimeout(() => {
            setSelectedFiles(prev => prev.filter(f => f.status !== 'success'));
            if (selectedFiles.every(f => f.status === 'success')) {
                setShowUploadModal(false);
            }
        }, 2000);
    };

    // Delete single reel
    const deleteReel = async (id) => {
        const content = `You are about to delete this reel. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        try {
            setLoader(true);
            const resp = await Calling.deleteReels({ id: [id] });
            if (resp?.status) {
                toast.success('Reel deleted successfully!');

            } else {
                toast.error(resp?.message || 'Failed to delete reel');
            }
        } catch (error) {
            console.log('Error:', error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    // Bulk Delete Functions
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = reelsData.map(reel => reel._id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const isAllSelected = reelsData.length > 0 && selectedIds.length === reelsData.length;
    const isIndeterminate = selectedIds.length > 0 && selectedIds.length < reelsData.length;

    // Bulk Delete with Captcha
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            toast.error('Please select at least one reel to delete');
            return;
        }

        const content = `You are about to delete ${selectedIds.length} reel(s). To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        try {
            setDeleteLoader(true);
            const resp = await Calling.deleteReels({ id: selectedIds });
            if (resp?.status) {
                toast.success(`${selectedIds.length} reel(s) deleted successfully!`);
                setSelectedIds([]);
                fetchData();
            } else {
                toast.error(resp?.message || 'Failed to delete reels');
            }
        } catch (error) {
            console.log('Error:', error);
            toast.error('Something went wrong');
        } finally {
            setDeleteLoader(false);
        }
    };

    // Clear all selected files
    const clearAllFiles = () => {
        selectedFiles.forEach(f => {
            if (f.preview) URL.revokeObjectURL(f.preview);
        });
        setSelectedFiles([]);
        setShowUploadModal(false);
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Reels Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'reels'} />
                <hr className='border-main-border' />
                <div className='px-4 py-4'>
                    <p className="text-main-subfont mb-2">Upload and manage video reels</p>

                    {/* Upload Section */}
                    <div
                        className={`bg-main-white rounded-xl border-2 border-dashed transition-all duration-300 p-8 mb-6 ${isDragging ? 'border-main-blue bg-main-th' : 'border-main-border'
                            }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className="text-center">
                            <FiUploadCloud className={`mx-auto text-5xl mb-4 ${isDragging ? 'text-main-blue' : 'text-main-subfont'}`} />
                            <h3 className="text-lg font-semibold text-main-font mb-2">
                                Drag & Drop Videos Here
                            </h3>
                            <p className="text-main-subfont text-sm mb-4">
                                or click to browse files
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="video/*"
                                className="hidden"
                                onChange={(e) => handleFileSelect(e.target.files)}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="bg-main-blue text-main-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity"
                            >
                                Select Videos
                            </button>
                            <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-main-subfont">
                                <span className="flex items-center gap-1">
                                    <FiVideo /> Max {MAX_FILES} files
                                </span>
                                <span>•</span>
                                <span>Max {MAX_SIZE_MB}MB per file</span>
                                <span>•</span>
                                <span>MP4, WebM, MOV, AVI</span>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-main-white rounded-xl border border-main-border p-4 mb-6">
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                                <div className="relative w-full sm:w-80">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-main-subfont" />
                                    <input
                                        type="text"
                                        placeholder="Search reels..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                    />
                                </div>
                                {/* Total Reels Count */}
                                {pagination && (
                                    <div className="flex items-center gap-2 px-3 py-2.5 bg-main-th rounded-lg">
                                        <FiVideo className="text-main-blue" />
                                        <span className="text-sm font-medium text-main-font">
                                            Total Reels: <span className="text-main-blue">{pagination.totalReels?.toLocaleString() || 0}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Bulk Delete Button */}
                                {selectedIds.length > 0 && (
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={deleteLoader}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-main-delete text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                                    >
                                        {deleteLoader ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <FiTrash2 />
                                                Delete ({selectedIds.length})
                                            </>
                                        )}
                                    </button>
                                )}
                                <button
                                    onClick={fetchData}
                                    className="flex items-center gap-2 px-4 py-2.5 border border-main-border rounded-lg text-main-font hover:bg-main-bg transition-colors"
                                >
                                    <FiRefreshCw className={loader ? 'animate-spin' : ''} />
                                    Refresh
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex items-center gap-2 bg-main-blue text-main-white px-4 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity"
                                >
                                    <FiUploadCloud />
                                    Upload Reels
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Reels Table */}
                    <div className="bg-main-white rounded-xl border border-main-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-main-th">
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font w-12">
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                ref={(el) => {
                                                    if (el) el.indeterminate = isIndeterminate;
                                                }}
                                                onChange={handleSelectAll}
                                                className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            />
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font">
                                            #
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font">
                                            Preview
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font">
                                            Name
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font">
                                            Views
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-semibold text-main-font">
                                            Created At
                                        </th>
                                        <th className="text-center px-4 py-3 text-sm font-semibold text-main-font">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-12">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-10 h-10 border-4 border-main-blue border-t-transparent rounded-full animate-spin"></div>
                                                    <span className="text-main-subfont">Loading reels...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : reelsData.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-12">
                                                <div className="flex flex-col items-center gap-3">
                                                    <FiVideo className="text-5xl text-main-subfont" />
                                                    <span className="text-main-subfont">No reels found</span>
                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="text-main-blue hover:underline text-sm"
                                                    >
                                                        Upload your first reel
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        reelsData.map((reel, index) => (
                                            <tr
                                                key={reel._id}
                                                className={`border-t border-main-border hover:bg-main-thHover transition-colors ${selectedIds.includes(reel._id) ? 'bg-main-th' : ''
                                                    }`}
                                            >
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(reel._id)}
                                                        onChange={() => handleSelectOne(reel._id)}
                                                        className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-main-font">
                                                    {(page - 1) * limit + index + 1}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div
                                                        className="w-16 h-20 rounded-lg overflow-hidden bg-main-bg cursor-pointer group relative"
                                                        onClick={() => setPreviewVideo(reel)}
                                                    >
                                                        <video
                                                            src={reel.r_video}
                                                            className="w-full h-full object-cover"
                                                            muted
                                                            preload="metadata"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                            <FiPlay className="text-white text-xl" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm font-medium text-main-font">
                                                        {reel.r_name}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-main-blue/10 text-main-blue rounded text-sm font-medium">
                                                        {formatView(reel?.r_view) || 0}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-main-subfont">
                                                    {formatDate(reel.createdAt)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => setPreviewVideo(reel)}
                                                            className="p-2 text-main-blue hover:bg-main-th rounded-lg transition-colors"
                                                            title="Preview"
                                                        >
                                                            <FiPlay />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteReel(reel._id)}
                                                            className="p-2 text-main-delete hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete"
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination && pagination.totalPages > 1 && (
                            <div className="px-4 py-3 border-t border-main-border flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-sm text-main-subfont">
                                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.totalReels)} of {pagination.totalReels} reels
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="px-3 py-1.5 border border-main-border rounded-lg text-sm text-main-font disabled:opacity-50 disabled:cursor-not-allowed hover:bg-main-bg transition-colors"
                                    >
                                        Previous
                                    </button>
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (page <= 3) {
                                            pageNum = i + 1;
                                        } else if (page >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = page - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setPage(pageNum)}
                                                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${page === pageNum
                                                    ? 'bg-main-blue text-main-white'
                                                    : 'border border-main-border text-main-font hover:bg-main-bg'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                                        disabled={page === pagination.totalPages}
                                        className="px-3 py-1.5 border border-main-border rounded-lg text-sm text-main-font disabled:opacity-50 disabled:cursor-not-allowed hover:bg-main-bg transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upload Modal */}
                    {showUploadModal && selectedFiles.length > 0 && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-main-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                                {/* Modal Header */}
                                <div className="px-6 py-4 border-b border-main-border flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-main-font">Upload Reels</h2>
                                        <p className="text-sm text-main-subfont">
                                            {selectedFiles.length} file(s) selected
                                        </p>
                                    </div>
                                    <button
                                        onClick={clearAllFiles}
                                        className="p-2 text-main-subfont hover:text-main-font hover:bg-main-bg rounded-lg transition-colors"
                                    >
                                        <FiX className="text-xl" />
                                    </button>
                                </div>

                                {/* File List */}
                                <div className="p-6 max-h-96 overflow-y-auto space-y-3">
                                    {selectedFiles.map((fileObj) => (
                                        <div
                                            key={fileObj.id}
                                            className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${fileObj.status === 'success'
                                                ? 'border-green-300 bg-green-50'
                                                : fileObj.status === 'error'
                                                    ? 'border-red-300 bg-red-50'
                                                    : 'border-main-border bg-main-bg'
                                                }`}
                                        >
                                            {/* Video Preview */}
                                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-main-border flex-shrink-0">
                                                <video
                                                    src={fileObj.preview}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* File Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-main-font truncate">
                                                    {fileObj.file.name}
                                                </p>
                                                <p className="text-xs text-main-subfont">
                                                    {formatFileSize(fileObj.file.size)}
                                                </p>

                                                {/* Progress Bar */}
                                                {(fileObj.status === 'uploading' || fileObj.status === 'success') && (
                                                    <div className="mt-2">
                                                        <div className="h-1.5 bg-main-border rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full transition-all duration-300 ${fileObj.status === 'success'
                                                                    ? 'bg-green-500'
                                                                    : 'bg-main-blue'
                                                                    }`}
                                                                style={{ width: `${fileObj.progress}%` }}
                                                            />
                                                        </div>
                                                        <p className="text-xs text-main-subfont mt-1">
                                                            {fileObj.status === 'success'
                                                                ? '✓ Uploaded successfully'
                                                                : `${fileObj.progress}% uploaded`
                                                            }
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Error Message */}
                                                {fileObj.status === 'error' && (
                                                    <p className="text-xs text-main-delete mt-1">
                                                        ✗ {fileObj.error || 'Upload failed'}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Remove Button */}
                                            {fileObj.status !== 'uploading' && (
                                                <button
                                                    onClick={() => removeFile(fileObj.id)}
                                                    className="p-2 text-main-subfont hover:text-main-delete hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                                >
                                                    <FiX />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Modal Footer */}
                                <div className="px-6 py-4 border-t border-main-border flex items-center justify-between gap-4">
                                    <button
                                        onClick={clearAllFiles}
                                        className="px-4 py-2.5 border border-main-border rounded-lg text-main-font hover:bg-main-bg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="px-4 py-2.5 border border-main-blue text-main-blue rounded-lg hover:bg-main-th transition-colors"
                                            disabled={uploadLoader}
                                        >
                                            Add More
                                        </button>
                                        <button
                                            onClick={uploadAllFiles}
                                            disabled={uploadLoader || selectedFiles.every(f => f.status === 'success')}
                                            className="flex items-center gap-2 bg-main-blue text-main-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {uploadLoader ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <FiUploadCloud />
                                                    Upload All
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Video Preview Modal */}
                    {previewVideo && (
                        <div
                            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                            onClick={() => setPreviewVideo(null)}
                        >
                            <div
                                className="bg-main-white rounded-xl w-full max-w-lg overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="px-4 py-3 border-b border-main-border flex items-center justify-between">
                                    <h3 className="font-semibold text-main-font">{previewVideo.r_name}</h3>
                                    <button
                                        onClick={() => setPreviewVideo(null)}
                                        className="p-2 text-main-subfont hover:text-main-font hover:bg-main-bg rounded-lg transition-colors"
                                    >
                                        <FiX />
                                    </button>
                                </div>
                                <div className="aspect-[9/16] max-h-[60vh] bg-black">
                                    <video
                                        src={previewVideo.r_video}
                                        controls
                                        autoPlay
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="p-4 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-main-subfont">Name:</span>
                                        <span className="text-main-font font-medium">{previewVideo.r_name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-main-subfont">Total Views:</span>
                                        <span className="text-main-font">{previewVideo.r_view?.toLocaleString() || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-main-subfont">Created:</span>
                                        <span className="text-main-font">{formatDate(previewVideo.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Reels;